import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Sparkles, Euro, Loader2, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuoteEstimator = ({ description, tradeCategory, city, country }) => {
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleEstimate = async () => {
    if (!description || description.length < 20) {
      toast.error('Ajoutez une description plus détaillée (min. 20 caractères)');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/estimate-quote`, {
        description,
        trade_category: tradeCategory,
        city,
        country
      }, { withCredentials: true });
      setEstimate(res.data);
    } catch (err) {
      toast.error('Erreur d\'estimation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-[#F59E0B]/20 bg-gradient-to-r from-[#F59E0B]/5 to-transparent" data-testid="quote-estimator">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#F59E0B]" />
          Estimation IA du budget
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!estimate ? (
          <div>
            <p className="text-sm text-[#4B5563] mb-3">
              L'IA analyse votre description pour estimer un budget réaliste basé sur les prix du marché.
            </p>
            <Button
              type="button"
              variant="outline"
              className="gap-2 border-[#F59E0B]/30 hover:bg-[#F59E0B]/5"
              onClick={handleEstimate}
              disabled={loading}
              data-testid="estimate-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-[#F59E0B]" />}
              {loading ? 'Analyse en cours...' : 'Estimer le budget'}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <div className="flex-1 p-3 bg-white rounded-lg border text-center">
                <p className="text-xs text-[#6B7280]">Min</p>
                <p className="text-lg font-bold text-[#111827]">{estimate.estimate_min?.toLocaleString('fr-FR')}€</p>
              </div>
              <TrendingUp className="w-5 h-5 text-[#6B7280]" />
              <div className="flex-1 p-3 bg-white rounded-lg border text-center">
                <p className="text-xs text-[#6B7280]">Max</p>
                <p className="text-lg font-bold text-[#111827]">{estimate.estimate_max?.toLocaleString('fr-FR')}€</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#6B7280]">Confiance:</span>
              <Badge className={
                estimate.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' :
                estimate.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                'bg-gray-100 text-gray-600'
              }>
                {estimate.confidence === 'high' ? 'Haute' : estimate.confidence === 'medium' ? 'Moyenne' : 'Basse'}
              </Badge>
            </div>
            {estimate.breakdown && (
              <div className="space-y-1.5">
                {estimate.breakdown.map((item, i) => (
                  <div key={item.item || i} className="flex items-center justify-between text-sm">
                    <span className="text-[#4B5563]">{item.item}</span>
                    <span className="font-medium text-[#111827]">{item.percentage}%</span>
                  </div>
                ))}
              </div>
            )}
            {estimate.notes && (
              <p className="text-xs text-[#6B7280] italic">{estimate.notes}</p>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={() => setEstimate(null)}
            >
              Nouvelle estimation
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QuoteEstimator;
